import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope, AnalyticsLive, AnalyticsSummary } from './models';
import { AuthService } from './auth.service';
import { I18nService } from './i18n/i18n.service';

const SID_KEY = 'se-sid';
const HEARTBEAT_MS = 20_000;

/**
 * First-party, anonymous visitor tracking. Fires a lightweight beacon on each
 * route change plus a periodic heartbeat so the dashboard can show who's live.
 * Admin screens and logged-in admins are never tracked.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private i18n = inject(I18nService);
  private base = environment.apiBase;

  private sessionId = this.loadSessionId();
  private currentPath = '';
  private started = false;

  /** Record a page view and (re)start the heartbeat for this path. */
  page(path: string): void {
    this.currentPath = path;
    if (!this.trackable(path)) return;
    this.send('view', path);
    this.startHeartbeat();
  }

  private startHeartbeat(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (!this.trackable(this.currentPath)) return;
      this.send('ping', this.currentPath);
    }, HEARTBEAT_MS);
  }

  private send(event: 'view' | 'ping', path: string): void {
    this.http
      .post(`${this.base}/analytics/collect`, {
        sessionId: this.sessionId,
        path,
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        lang: this.i18n.lang(),
        event,
      })
      .subscribe({ next: () => {}, error: () => {} });
  }

  /** Skip admin routes and any session where an admin is signed in. */
  private trackable(path: string): boolean {
    if (!path || path.startsWith('/admin')) return false;
    if (this.auth.isAuthenticated()) return false;
    return true;
  }

  private loadSessionId(): string {
    try {
      let id = localStorage.getItem(SID_KEY);
      if (!id) {
        id = this.randomId();
        localStorage.setItem(SID_KEY, id);
      }
      return id;
    } catch {
      return this.randomId();
    }
  }

  private randomId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'sid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  // ---- Admin dashboard reads ----
  getLive(): Observable<AnalyticsLive> {
    return this.http
      .get<ApiEnvelope<AnalyticsLive>>(`${this.base}/analytics/live`)
      .pipe(map((r) => r.data));
  }

  getSummary(range: string): Observable<AnalyticsSummary> {
    return this.http
      .get<ApiEnvelope<AnalyticsSummary>>(`${this.base}/analytics/summary`, { params: { range } })
      .pipe(map((r) => r.data));
  }
}
